import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

import { getNativeUser, requireUser, serializeNativeError } from './helpers';

import type { FirebaseContext } from './context';
import type {
  ActionCodeSettings,
  FirebaseAccountMethods,
  FirebaseAuthModule,
  RecaptchaVerifierOptions
} from './types';

interface FirebaseAccountDeps {
  importAuth: () => Promise<FirebaseAuthModule>;
  user: () => Promise<import('firebase/auth').User | null>;
  isNativeAuth: () => boolean;
}

export function createFirebaseAccountMethods(
  context: FirebaseContext,
  deps: FirebaseAccountDeps
): FirebaseAccountMethods {
  const { log } = context;

  return {
    resetPassword: async email => {
      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.sendPasswordResetEmail(await firebaseAuth.getAuth(), email);
    },

    sendEmailVerification: async (settings?: ActionCodeSettings | null) => {
      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native sendEmailVerification()', settings);
          return FirebaseAuthentication.sendEmailVerification(settings ?? undefined);
        }
      }

      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.sendEmailVerification(
        await requireUser(deps.user, 'send email verification'),
        settings
      );
    },

    checkActionCode: async actionCode => {
      log.info('checkActionCode');

      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native checkActionCode()', actionCode);
          return FirebaseAuthentication.checkActionCode({ oobCode: actionCode });
        }
      }

      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.checkActionCode(await firebaseAuth.getAuth(), actionCode);
    },

    applyActionCode: async actionCode => {
      log.info('applyActionCode');

      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native applyActionCode()', actionCode);
          return FirebaseAuthentication.applyActionCode({ oobCode: actionCode });
        }
      }

      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.applyActionCode(await firebaseAuth.getAuth(), actionCode);
    },

    confirmPasswordReset: async (actionCode, password) => {
      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.confirmPasswordReset(await firebaseAuth.getAuth(), actionCode, password);
    },

    changeEmail: async newEmail => {
      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native verifyBeforeUpdateEmail()', { nativeUid: nativeUser.uid });
          try {
            return await FirebaseAuthentication.verifyBeforeUpdateEmail({ newEmail });
          } catch (error) {
            log.error('native verifyBeforeUpdateEmail() failed', serializeNativeError(error));
            throw error;
          }
        }
        log.warn('changeEmail: isNativeAuth() is true but no native user found; falling back to web SDK');
      }

      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.verifyBeforeUpdateEmail(await requireUser(deps.user, 'change email'), newEmail, {
        url: 'https://app.nbx.com',
        handleCodeInApp: true
      });
    },

    changePassword: async password => {
      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native changePassword()');
          return FirebaseAuthentication.updatePassword({ newPassword: password });
        }
      }

      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.updatePassword(await requireUser(deps.user, 'change password'), password);
    },

    deleteUser: async () => {
      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native deleteUser()');
          return FirebaseAuthentication.deleteUser();
        }
      }

      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.deleteUser(await requireUser(deps.user, 'delete user'));
    },

    createRecaptchaVerifier: async ({
      containerId,
      options
    }: {
      containerId: string;
      options: RecaptchaVerifierOptions;
    }) => {
      const firebaseAuth = await deps.importAuth();
      return new firebaseAuth.RecaptchaVerifier(
        await firebaseAuth.getAuth(),
        containerId,
        window.PLATFORM === 'ios' ? { ...options, 'auth-type': 'ios' } : options
      );
    }
  };
}
